/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Button, Stack, Text, TextInput, useMantineTheme } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { modals } from '@mantine/modals';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import i18n from '@fai/friendli-suite/features/i18n';
import { TextVariants } from '@fai/ui/components/typography/variants';
import { FooterlessModalFooter } from '@fai/ui/mantine-modal/helpers';
import MessageBox from '@fai/ui/views/common/message-box';
import { MessageBoxVariant } from '@fai/ui/views/common/message-box/variants';

import { useDeleteEndpoint } from './use-delete-endpoint';

const deleteEndpointModalId = 'dedicated-endpoints-project-endpoints-detail-delete-endpoint-modal';

export const openDeleteEndpointModal = (props: { endpointName?: string | null }) => {
  modals.openContextModal({
    modal: 'footerless',
    modalId: deleteEndpointModalId,
    title: i18n.t('dedicated_endpoints.project.delete_endpoint'),
    innerProps: { modalBody: <DeleteEndpointModalBody {...props} /> },
  });
};

function DeleteEndpointModalBody({ endpointName }: { endpointName?: string | null }) {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const form = useForm({
    initialValues: { name: '' },
    validate: zodResolver(
      z.object({
        name: z.string().refine((value) => value === endpointName, {
          message: `${t('dedicated_endpoints.project.endpoint_name_mismatch')}`,
        }),
      })
    ),
  });

  const { deleteEndpoint, loading: deleteEndpointLoading } = useDeleteEndpoint({
    onCompleted: () => modals.close(deleteEndpointModalId),
  });

  return (
    <Stack gap={theme.pf.layout.xs}>
      <MessageBox align="flex-start" variant={MessageBoxVariant.Danger}>
        {t('dedicated_endpoints.project.delete_endpoint_action_cannot_be_undone')}
      </MessageBox>

      <Stack gap={theme.pf.layout['2xs']}>
        <Text c={theme.pf.color.fg.neutral.strong} variant={TextVariants.TitleXs}>
          <Trans
            components={[<b />]}
            i18nKey="dedicated_endpoints.project.please_type_to_confirm_endpoint"
            values={{ endpointName }}
          />
        </Text>

        <TextInput
          {...form.getInputProps('name')}
          placeholder={t('dedicated_endpoints.project.enter_endpoint_name') || ''}
          onBlur={form.validate}
        />
      </Stack>

      <FooterlessModalFooter>
        <Button variant="transparent" onClick={() => modals.close(deleteEndpointModalId)}>
          {t('shared.cancel')}
        </Button>

        <Button
          theme="danger"
          disabled={!form.isValid()}
          loading={deleteEndpointLoading}
          onClick={deleteEndpoint}
        >
          {t('shared.delete')}
        </Button>
      </FooterlessModalFooter>
    </Stack>
  );
}

DeleteEndpointModalBody.displayName = 'DeleteEndpointModalBody';
